import type {
  PrimeUiInspectPageReportRow,
  PrimeUiProjectPageDetailsResult,
} from "../types.js";

function normalizeTextValue(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function extractFirstStringByKey(
  value: unknown,
  key: "title" | "description",
  seen: Set<unknown>,
): string | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  if (seen.has(value)) {
    return null;
  }
  seen.add(value);

  if (Array.isArray(value)) {
    for (const item of value) {
      const nested = extractFirstStringByKey(item, key, seen);
      if (nested) {
        return nested;
      }
    }
    return null;
  }

  const record = value as Record<string, unknown>;
  for (const [recordKey, recordValue] of Object.entries(record)) {
    if (recordKey.toLowerCase() === key) {
      const normalized = normalizeTextValue(recordValue);
      if (normalized) {
        return normalized;
      }
    }
  }

  for (const nestedValue of Object.values(record)) {
    const nested = extractFirstStringByKey(nestedValue, key, seen);
    if (nested) {
      return nested;
    }
  }

  return null;
}

function extractTitleAndDescription(
  props: Record<string, unknown> | null,
): Pick<PrimeUiInspectPageReportRow, "title" | "description"> {
  if (!props) {
    return {
      title: null,
      description: null,
    };
  }

  return {
    title: extractFirstStringByKey(props, "title", new Set()),
    description: extractFirstStringByKey(props, "description", new Set()),
  };
}

function resolveSummaryText(
  row: Pick<PrimeUiInspectPageReportRow, "title" | "description">,
): string {
  return row.title ?? row.description ?? "";
}

export function buildInspectPageReport(
  input: PrimeUiProjectPageDetailsResult,
): {
  report: string;
  reportRows: PrimeUiInspectPageReportRow[];
} {
  if (input.components === null) {
    return {
      report:
        "Components are unavailable for this page because no active variant is set (isReadyToExport=false).",
      reportRows: [],
    };
  }

  if (input.components.length === 0) {
    return {
      report: "Active variant has no components.",
      reportRows: [],
    };
  }

  const reportRows = input.components.map((component, index) => {
    const titleDescription = extractTitleAndDescription(component.props);
    return {
      number: index + 1,
      componentGroup: component.componentGroup,
      componentId: component.componentId,
      blockId: component.blockId,
      title: titleDescription.title,
      description: titleDescription.description,
    };
  });

  const report = reportRows
    .map(
      (row) =>
        `${row.number}. ${row.componentGroup} (${row.componentId}) | ${resolveSummaryText(row)} | ${row.blockId}`,
    )
    .join("\n");

  return {
    report,
    reportRows,
  };
}
